// ===== Packages =====

import Stripe from 'stripe';

// ===== Interfaces =====

import {
    ICartItem,
    IOrderCost,
    IShippoRateObject,
    IStoreItem,
} from '../interfaces';

// ===== Services =====

import applyPromotionToCheckoutItem from './applyPromotionToCheckoutItem';

const CENTS_IN_A_DOLLAR = 100;
const VERASCOPE_APPLIES_ONCE_PER_ORDER_METADATA_KEY = 'verascopeAppliesOncePerOrder';

interface IBuildFallbackOrderCostPaymentIntent {
    amount?: number | null,
    metadata?: {
        shippingRate?: string,
        shippingTax?: string,
    },
}

interface ICalculateDiscountedCartSubtotalArgs {
    cartItems: ICartItem[],
    promotions: Stripe.Coupon[],
    storeItemsByPrice: Map<string, IStoreItem>,
    promotionAppliesOncePerOrder?: (promotion: Stripe.Coupon) => boolean,
}

function promotionAppliesOncePerOrder(promotion: Stripe.Coupon): boolean {
    return promotion.metadata?.[VERASCOPE_APPLIES_ONCE_PER_ORDER_METADATA_KEY] === 'true';
}

function safeParseShippingRate(
    shippingRateValue?: string,
): IShippoRateObject | undefined {
    if (!shippingRateValue) return undefined;

    try {
        return JSON.parse(shippingRateValue) as IShippoRateObject;
    } catch (_error) {
        return undefined;
    }
}

function getShippingAmount(
    shippingRate?: IShippoRateObject,
): number | undefined {
    const shippingAmount = shippingRate?.amount
        ? parseFloat(shippingRate.amount)
        : Number.NaN;
    if (!Number.isFinite(shippingAmount)) return undefined;

    return shippingAmount * CENTS_IN_A_DOLLAR;
}

function getShippingTax(
    shippingTaxValue?: string,
): number | undefined {
    if (!shippingTaxValue) return undefined;

    const shippingTax = parseInt(shippingTaxValue, 10);
    if (!Number.isFinite(shippingTax)) return undefined;

    return shippingTax;
}

/**
 * Builds a best-effort order-cost snapshot from Stripe payment-intent metadata
 * so checkout can render stable totals even before a tax calculation is
 * resolved or when only canonical Stripe amount data is available.
 */
export function buildFallbackOrderCostFromPaymentIntent(
    paymentIntent?: IBuildFallbackOrderCostPaymentIntent | null,
): IOrderCost | undefined {
    if (typeof paymentIntent?.amount !== 'number') return undefined;

    const shippingRate = safeParseShippingRate(paymentIntent.metadata?.shippingRate);
    const shipping = getShippingAmount(shippingRate);
    const shippingTax = getShippingTax(paymentIntent.metadata?.shippingTax);
    let fallbackOrderCostTotals: Partial<IOrderCost> = {};
    if (shippingTax != null) {
        fallbackOrderCostTotals = {
            tax: shippingTax,
            total: paymentIntent.amount,
        };
    } else if (shipping != null) {
        fallbackOrderCostTotals = {
            total: paymentIntent.amount,
        };
    }

    return {
        subtotal: Math.max(
            0,
            paymentIntent.amount - (shipping || 0) - (shippingTax || 0),
        ),
        ...(shipping != null ? {
            shipping,
        } : {}),
        ...fallbackOrderCostTotals,
    };
}

/**
 * Mirrors the server's promotion math for the UI-only subtotal fallback. Each
 * once-per-order promotion is consumed only by the first eligible unit it
 * actually discounts, so mixed carts remain correct regardless of item order.
 */
export function calculateDiscountedCartSubtotal({
    cartItems,
    promotions,
    storeItemsByPrice,
    promotionAppliesOncePerOrder: promotionAppliesOncePerOrderOverride = (
        promotionAppliesOncePerOrder
    ),
}: ICalculateDiscountedCartSubtotalArgs): number {
    const appliedOncePerOrderPromotionIds = new Set<string>();

    return cartItems.reduce((
        total: number,
        item: ICartItem,
    ) => {
        const storeItem = storeItemsByPrice.get(item.saleNegotiationPriceId || item.priceId);
        if (!storeItem || !storeItem.amount) return total;

        let cumulativeAmount = 0;
        for (let quantityIndex = 0; quantityIndex < item.quantity; quantityIndex += 1) {
            let cumulativeDiscount = 0;
            for (let promotionIndex = 0; promotionIndex < promotions.length; promotionIndex += 1) {
                const promotion = promotions[promotionIndex];
                const isOncePerOrderPromotion = promotionAppliesOncePerOrderOverride(promotion);
                if (
                    !(
                        isOncePerOrderPromotion
                        && appliedOncePerOrderPromotionIds.has(promotion.id)
                    )
                ) {
                    const discountedAmount = applyPromotionToCheckoutItem(
                        storeItem,
                        promotion,
                    );
                    const discountAmount = storeItem.amount - discountedAmount;
                    cumulativeDiscount += discountAmount;

                    if (
                        isOncePerOrderPromotion
                        && discountAmount > 0
                    ) {
                        appliedOncePerOrderPromotionIds.add(promotion.id);
                    }
                }
            }
            cumulativeAmount += storeItem.amount - cumulativeDiscount;
        }
        return total + cumulativeAmount;
    }, 0);
}
